import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import { ServicesOverview, ServiceCategoryPage } from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import About from "@/pages/About";
import Work from "@/pages/Work";
import Legal, { LEGAL_DOCS } from "@/pages/Legal";
import SeoPricing from "@/pages/SeoPricing";
import EliteSeoPackages from "@/pages/EliteSeoPackages";
import AdminLayout from "@/pages/admin/AdminLayout";
import { AdminLogin, AdminForgot, AdminReset } from "@/pages/admin/AdminAuth";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminServices from "@/pages/admin/AdminServices";
import AdminPosts from "@/pages/admin/AdminPosts";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPortfolio from "@/pages/admin/AdminPortfolio";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminProjectDetail from "@/pages/admin/AdminProjectDetail";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSettings from "@/pages/admin/AdminSettings";
import { Resources, ArticlePage } from "@/pages/Resources";
import { NotFound } from "@/pages/Placeholders";
import ChatWidget from "@/components/ChatWidget";
import { useLenis } from "@/hooks/useLenis";
import { api } from "@/lib/api";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// Applies admin-managed redirect rules client-side (301-equivalent for the SPA).
const RedirectCheck = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);

  useEffect(() => {
    api.get("/redirects").then((r) => setRules(r.data)).catch(() => setRules([]));
  }, []);

  useEffect(() => {
    if (!rules) return;
    const match = rules.find((r) => r.from_path === pathname);
    if (match) {
      if (match.to_path.startsWith("http")) window.location.replace(match.to_path);
      else navigate(match.to_path, { replace: true });
    }
  }, [pathname, rules, navigate]);
  return null;
};

const Layout = () => (
  <div className="flex min-h-screen flex-col bg-brand-black">
    <Header />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
    <ChatWidget />
  </div>
);

function App() {
  useLenis();
  return (
    <div className="App">
      <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ScrollToTop />
        <RedirectCheck />
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot" element={<AdminForgot />} />
          <Route path="/admin/reset" element={<AdminReset />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="projects/:projectId" element={<AdminProjectDetail />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="enquiries" element={<AdminEnquiries />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="portfolio" element={<AdminPortfolio />} />
            <Route path="posts" element={<AdminPosts />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="legal" element={<AdminLegal />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesOverview />} />
            <Route path="/services/:category" element={<ServiceCategoryPage />} />
            <Route path="/services/:category/:service" element={<ServiceDetail />} />
            <Route path="/work" element={<Work />} />
            <Route path="/seo-pricing" element={<SeoPricing />} />
            <Route path="/elite-seo-packages" element={<EliteSeoPackages />} />
            <Route path="/about" element={<About />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ArticlePage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            {LEGAL_DOCS.map((d) => (
              <Route key={d.key} path={d.path} element={<Legal docKey={d.key} />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </MotionConfig>
    </div>
  );
}

export default App;
